import { ref } from "vue";
import { useToast } from "primevue/usetoast";
import { adminV1OrganizationsProfilesCreate } from "@/api/admin-profiles/admin-profiles";
import { adminV1OrganizationsProfilesTeamsCreate } from "@/api/admin-profiles/admin-profiles";
import type { AdminProfile, ProfileRole } from "@/api/model";
import { addMixpanelEvent, EVENT_NAMES } from "@/shared/utils/analytics";

interface AssociatedContact {
  contact_name: string;
  is_primary: boolean;
  linked_profile_ids: number[];
}

interface CreateUserData {
  name: string;
  // Omitted for managed child athletes without their own login (MAI-4171)
  email?: string;
  phone_number?: string;
  organization_id: number;
  roles?: ProfileRole[];
  is_admin?: boolean;
  /** Payments tier (MAI-4956); only meaningful with is_admin. */
  can_manage_payments?: boolean;
  associated_contact?: AssociatedContact;
  // Required by the backend for managed athletes (no email): the guardian
  // contact linked atomically at creation (MAI-4171).
  guardian_contact_id?: number;
}

export function useCreateUser() {
  const toast = useToast();
  const isSubmitting = ref(false);

  async function createUser(
    userData: CreateUserData,
    teamIds?: number[]
  ): Promise<AdminProfile | null> {
    isSubmitting.value = true;

    try {
      // Create the user profile
      const result = await adminV1OrganizationsProfilesCreate(
        userData.organization_id,
        {
          name: userData.name,
          email: userData.email,
          phone_number: userData.phone_number,
          organization_id: userData.organization_id,
          roles: userData.roles,
          is_admin: userData.is_admin,
          can_manage_payments: userData.can_manage_payments,
          associated_contact: userData.associated_contact,
          guardian_contact_id: userData.guardian_contact_id,
        } as any
      );

      // If teams were selected, add the user to each team
      if (teamIds && teamIds.length > 0 && result.id) {
        try {
          console.log("Adding user to teams:", {
            orgId: userData.organization_id,
            userId: result.id,
            teamIds,
          });

          // Add user to each team
          const teamPromises = teamIds.map((teamId) =>
            adminV1OrganizationsProfilesTeamsCreate(
              userData.organization_id,
              result.id,
              teamId
            )
          );

          await Promise.all(teamPromises);
          console.log("Successfully added user to all teams");
        } catch (teamError) {
          console.error("Error adding user to teams:", teamError);
          // Still show success for user creation, but mention team issue
          toast.add({
            severity: "warn",
            summary: "User Created",
            detail:
              "User created successfully, but failed to add to one or more teams",
            life: 5000,
          });
          return result;
        }
      } else {
        console.log("No teams to add user to:", {
          teamIds,
          resultId: result.id,
        });
      }

      toast.add({
        severity: "success",
        summary: "Success",
        detail: "User created successfully",
        life: 3000,
      });

      // Track the event
      addMixpanelEvent(EVENT_NAMES.USER.UserCreated);
      addMixpanelEvent(EVENT_NAMES.USER.UserAddedToOrganization, {
        organizationId: userData.organization_id,
        isAdmin: Boolean(userData.is_admin),
        roles: userData.roles ?? [],
      });

      return result;
    } catch (error: any) {
      console.error("Error creating user:", error);

      // A 409 means the user already has a profile in this organization.
      // The axios client rejects conflicts as { status, data }, not error.response.
      if (error?.status === 409) {
        toast.add({
          severity: "error",
          summary: "Already in this organization",
          detail:
            error?.data?.detail ||
            "This person already has a profile in this organization.",
          life: 5000,
        });
        return null;
      }

      let errorMessage = "Failed to create user";

      // Extract error message from response
      if (error?.response?.data) {
        const data = error.response.data;
        if (typeof data === "string") {
          errorMessage = data;
        } else if (data.email) {
          errorMessage = `Email: ${Array.isArray(data.email) ? data.email[0] : data.email}`;
        } else if (data.name) {
          errorMessage = `Name: ${Array.isArray(data.name) ? data.name[0] : data.name}`;
        } else if (data.roles) {
          // e.g. "Athletes without an email can only have the PLAYER role."
          errorMessage = Array.isArray(data.roles) ? data.roles[0] : data.roles;
        } else if (data.guardian_contact_id) {
          // e.g. "An athlete without an email must be linked to a parent or guardian."
          errorMessage = Array.isArray(data.guardian_contact_id)
            ? data.guardian_contact_id[0]
            : data.guardian_contact_id;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.error) {
          // e.g. "Email is required" when the org can't create managed athletes
          errorMessage = Array.isArray(data.error) ? data.error[0] : data.error;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.add({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
        life: 5000,
      });

      return null;
    } finally {
      isSubmitting.value = false;
    }
  }

  return {
    isSubmitting,
    createUser,
  };
}
